import { keyBy } from '@exodus/basic-utils';
import { Address } from '@exodus/models';
import pDefer from 'p-defer';
let isEthereumLikeAsset;
try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports, @exodus/hydra/no-require
    isEthereumLikeAsset = require('@exodus/ethereum-lib').isEthereumLikeAsset;
}
catch {
    console.log('Optional module @exodus/ethereum-lib not found, continuing without it.');
}
export class AddressProvider {
    #addressesCacheAtom;
    #assetsModule;
    #deferredFetch = pDefer();
    constructor({ addressesCacheAtom, assetsModule, }) {
        this.#addressesCacheAtom = addressesCacheAtom;
        this.#assetsModule = assetsModule;
    }
    #getCache = async () => {
        let cache = await this.#addressesCacheAtom.get();
        if (!cache) {
            // If atom is not populated, it means we need to wait for `fetch` to run.
            await this.#deferredFetch.promise;
            cache = await this.#addressesCacheAtom.get();
        }
        return cache;
    };
    #getCacheKey = ({ assetName, purpose, chainIndex }) => `${assetName}-${purpose}-${chainIndex}`;
    #normalizeOptions = (opts) => {
        // Use the base asset name for custom tokens.
        let assetName = opts.assetName;
        const asset = this.#assetsModule.getAsset(assetName);
        if (asset.isCustomToken) {
            assetName = asset.baseAssetName;
        }
        // Use the default purpose if not provided.
        const defaultPurpose = this.getDefaultPurpose(opts);
        const purpose = opts.purpose ?? defaultPurpose;
        // Use the default chain index if not provided.
        const chainIndex = opts.chainIndex ?? 0;
        const normalizedOpts = {
            ...opts,
            assetName,
            purpose,
            chainIndex,
        };
        return normalizedOpts;
    };
    load = async (addresses) => {
        const freshCache = keyBy(addresses, 'key');
        await this.#addressesCacheAtom.set(freshCache);
        this.#deferredFetch.resolve();
    };
    async getAddress({ assetName, purpose, chainIndex }) {
        const { baseAsset } = this.#assetsModule.getAsset(assetName);
        const cache = await this.#getCache();
        const cacheKey = this.#getCacheKey({
            assetName: baseAsset.name,
            purpose,
            chainIndex,
        });
        let { address } = cache[cacheKey] ?? {};
        // Currently, the addresses cache is not updating when new assets are added.
        // This workaround works for EVMs only because they utilize the same address.
        if (!address && isEthereumLikeAsset?.(baseAsset)) {
            const cacheKey = this.#getCacheKey({
                assetName: 'ethereum',
                purpose,
                chainIndex,
            });
            address = cache[cacheKey]?.address;
        }
        return address ? Address.fromJSON(address) : null;
    }
    getSupportedPurposes = (opts) => {
        const { baseAsset } = this.#assetsModule.getAsset(opts.assetName);
        return baseAsset.api?.getSupportedPurposes?.(opts);
    };
    getDefaultPurpose = (opts) => {
        const purposes = this.getSupportedPurposes(opts);
        return purposes[0];
    };
    getUnusedAddressIndexes = (opts) => {
        const purposes = this.getSupportedPurposes(opts);
        return purposes.map((purpose) => ({ purpose, chain: [0, 0] }));
    };
    getReceiveAddress = async (opts) => {
        const normalizedOpts = this.#normalizeOptions(opts);
        return this.getAddress({ ...normalizedOpts, chainIndex: 0 });
    };
    getChangeAddress = async (opts) => {
        const normalizedOpts = this.#normalizeOptions(opts);
        return this.getAddress({ ...normalizedOpts, chainIndex: 1 });
    };
    getReceiveAddresses = async ({ assetName }) => {
        return [await this.getReceiveAddress({ assetName })];
    };
}
const addressProviderModuleDefinition = {
    id: 'addressProvider',
    type: 'module',
    factory: (args) => new AddressProvider(args),
    public: true,
    dependencies: ['assetsModule', 'addressesCacheAtom'],
};
export default addressProviderModuleDefinition;
